import { actionTypes } from "../../app/store/ducks/auth.duck";
import { me, refresh } from "../../app/crud/auth.crud";
import { stepUpEvents } from "../../app/utils/stepUpEvents";

export function removeCSSClass(ele, cls) {
  const reg = new RegExp("(\\s|^)" + cls + "(\\s|$)");
  ele.className = ele.className.replace(reg, " ");
}

export function addCSSClass(ele, cls) {
  ele.classList.add(cls);
}

export const toAbsoluteUrl = (pathname) => process.env.PUBLIC_URL + pathname;

const isBackendRequest = (url) => {
  if (!url) return false;
  const baseUrl = process.env.REACT_APP_URL || "";
  return url.startsWith(baseUrl) || url.startsWith("/");
};

const isValidAuthToken = (token) =>
  typeof token === "string" &&
  token !== "undefined" &&
  token !== "null" &&
  token.length > 0;

const getStoredAuthToken = () => {
  const authToken = localStorage.getItem("authToken");
  return isValidAuthToken(authToken) ? authToken : undefined;
};

export function setupAxios(axios, store) {
  axios.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const authTokenFromState = state?.auth?.authToken;
      const authToken =
        (isValidAuthToken(authTokenFromState) && authTokenFromState) ||
        getStoredAuthToken();

      if (isBackendRequest(config.url)) {
        config.headers = config.headers || {};
        config.withCredentials = true;
        config.headers["X-Context"] = "barbeiro";
      } else {
        config.withCredentials = false;
      }

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      return config;
    },
    (err) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    function(response) {
      const migrate = response.data?._migrate;

      if (migrate?.clearBearer) {
        store.dispatch({ type: actionTypes.ClearLegacyToken });
      }

      return response;
    },
    async function(error) {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._isAuthRoute) {
        try {
          await me();
        } catch (meError) {
          const responseData = meError.response?.data;
          const hasRefreshAction = responseData?.action === "refresh_required";

          if (hasRefreshAction) {
            try {
              await refresh();
            } catch {
              store.dispatch({ type: actionTypes.Logout, payload: null });
            }
          } else {
            store.dispatch({ type: actionTypes.Logout, payload: null });
          }
        }
      }

      if (error.response?.status === 403) {
        const errorCode = error.response?.data?.code;

        if (errorCode === "step_up_required") {
          const bucket = error.response?.data?.bucket || "security";

          try {
            await stepUpEvents.requestStepUp(bucket, originalRequest);
            return axios(originalRequest);
          } catch {
            return Promise.reject(error);
          }
        }

        window.location.href = "/error/error-v3";
      }

      if (error.response?.status === 402) {
        window.location.href = "/error/error-v7";
      }

      return Promise.reject(error);
    }
  );
}

export function removeStorage(key) {
  try {
    localStorage.setItem(key, "");
    localStorage.setItem(key + "_expiresIn", "");
  } catch (e) {
    console.log(
      "removeStorage: Error removing key [" +
        key +
        "] from localStorage: " +
        JSON.stringify(e)
    );
    return false;
  }
  return true;
}

export function getStorage(key) {
  const now = Date.now(); //epoch time, lets deal only with integer
  // set expiration for storage
  let expiresIn = localStorage.getItem(key + "_expiresIn");
  if (expiresIn === undefined || expiresIn === null) {
    expiresIn = 0;
  }

  expiresIn = Math.abs(expiresIn);
  if (expiresIn < now) {
    // Expired
    removeStorage(key);
    return null;
  } else {
    try {
      const value = localStorage.getItem(key);
      return value;
    } catch (e) {
      console.log(
        "getStorage: Error reading key [" +
          key +
          "] from localStorage: " +
          JSON.stringify(e)
      );
      return null;
    }
  }
}

export function setStorage(key, value, expires) {
  if (expires === undefined || expires === null) {
    expires = 24 * 60 * 60; // default: seconds for 1 day
  }

  const now = Date.now(); //millisecs since epoch time, lets deal only with integer
  const schedule = now + expires * 1000;
  try {
    localStorage.setItem(key, value);
    localStorage.setItem(key + "_expiresIn", schedule);
  } catch (e) {
    console.log(
      "setStorage: Error setting key [" +
        key +
        "] in localStorage: " +
        JSON.stringify(e)
    );
    return false;
  }
  return true;
}
